/**
 * Height of the built surfaces a body can stand on: road ribbons, bridge
 * decks, piers and pitches.
 *
 * The terrain is not the whole ground. Roads are draped a few decimetres above
 * it, and a bridge deck can be ten metres above the river under it. Anything
 * that walks or drives needs the surface it is actually on, so this indexes
 * those triangles in a coarse spatial hash and answers "how high is the
 * surface here" in a handful of point-in-triangle tests.
 *
 * Surfaces are only offered below a ceiling the caller gives — its own height
 * plus a step. That is what lets a player walk up a bridge's approach onto the
 * deck, while a player walking along the road *under* the bridge is not
 * snatched up onto it.
 */

const CELL = 8;

export interface SurfaceMesh {
  /** x, y, z triples in the index's local frame. */
  positions: Float32Array;
  indices: Uint32Array | Uint16Array;
}

export class SurfaceIndex {
  private readonly head: Int32Array;
  private readonly next: Int32Array;
  private readonly tri: Int32Array;
  private readonly originCx: number;
  private readonly originCz: number;
  private readonly cols: number;
  private readonly rows: number;
  /** Flat triangle store: 9 floats a triangle. */
  private readonly tris: Float32Array;
  readonly minX: number;
  readonly minZ: number;
  readonly maxX: number;
  readonly maxZ: number;

  constructor(meshes: SurfaceMesh[]) {
    let count = 0;
    for (const m of meshes) count += Math.floor(m.indices.length / 3);
    this.tris = new Float32Array(count * 9);
    if (count === 0) {
      this.head = new Int32Array(0);
      this.next = new Int32Array(0);
      this.tri = new Int32Array(0);
      this.originCx = 0; this.originCz = 0; this.cols = 0; this.rows = 0;
      this.minX = Infinity; this.minZ = Infinity; this.maxX = -Infinity; this.maxZ = -Infinity;
      return;
    }
    let t = 0;
    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    let totalEntries = 0;
    for (const m of meshes) {
      const p = m.positions, ind = m.indices;
      for (let i = 0; i + 2 < ind.length; i += 3, t++) {
        const o = t * 9;
        const v0 = ind[i] * 3, v1 = ind[i + 1] * 3, v2 = ind[i + 2] * 3;
        const ax = p[v0], ay = p[v0 + 1], az = p[v0 + 2];
        const bx = p[v1], by = p[v1 + 1], bz = p[v1 + 2];
        const cx = p[v2], cy = p[v2 + 1], cz = p[v2 + 2];
        this.tris[o] = ax; this.tris[o + 1] = ay; this.tris[o + 2] = az;
        this.tris[o + 3] = bx; this.tris[o + 4] = by; this.tris[o + 5] = bz;
        this.tris[o + 6] = cx; this.tris[o + 7] = cy; this.tris[o + 8] = cz;
        const x0 = ax < bx ? (ax < cx ? ax : cx) : (bx < cx ? bx : cx);
        const x1 = ax > bx ? (ax > cx ? ax : cx) : (bx > cx ? bx : cx);
        const z0 = az < bz ? (az < cz ? az : cz) : (bz < cz ? bz : cz);
        const z1 = az > bz ? (az > cz ? az : cz) : (bz > cz ? bz : cz);
        if (x0 < minX) minX = x0;
        if (x1 > maxX) maxX = x1;
        if (z0 < minZ) minZ = z0;
        if (z1 > maxZ) maxZ = z1;
        totalEntries += (Math.floor(x1 / CELL) - Math.floor(x0 / CELL) + 1) * (Math.floor(z1 / CELL) - Math.floor(z0 / CELL) + 1);
      }
    }
    this.minX = minX; this.minZ = minZ; this.maxX = maxX; this.maxZ = maxZ;
    const originCx = Math.floor(minX / CELL);
    const originCz = Math.floor(minZ / CELL);
    const cols = Math.floor(maxX / CELL) - originCx + 1;
    const rows = Math.floor(maxZ / CELL) - originCz + 1;
    this.originCx = originCx;
    this.originCz = originCz;
    this.cols = cols;
    this.rows = rows;
    const head = new Int32Array(cols * rows).fill(-1);
    const next = new Int32Array(totalEntries);
    const tri = new Int32Array(totalEntries);
    let e = 0;
    for (let ti = 0; ti < count; ti++) {
      const o = ti * 9;
      const ax = this.tris[o], az = this.tris[o + 2];
      const bx = this.tris[o + 3], bz = this.tris[o + 5];
      const cx = this.tris[o + 6], cz = this.tris[o + 8];
      const cx0 = Math.floor((ax < bx ? (ax < cx ? ax : cx) : (bx < cx ? bx : cx)) / CELL) - originCx;
      const cx1 = Math.floor((ax > bx ? (ax > cx ? ax : cx) : (bx > cx ? bx : cx)) / CELL) - originCx;
      const cz0 = Math.floor((az < bz ? (az < cz ? az : cz) : (bz < cz ? bz : cz)) / CELL) - originCz;
      const cz1 = Math.floor((az > bz ? (az > cz ? az : cz) : (bz > cz ? bz : cz)) / CELL) - originCz;
      for (let gx = cx0; gx <= cx1; gx++) {
        for (let gz = cz0; gz <= cz1; gz++) {
          const cell = gz * cols + gx;
          tri[e] = ti;
          next[e] = head[cell];
          head[cell] = e++;
        }
      }
    }
    this.head = head;
    this.next = next;
    this.tri = tri;
  }

  /**
   * The highest surface at (x, z) that is no higher than `ceiling`, or
   * undefined when none is.
   */
  heightAt(x: number, z: number, ceiling: number): number | undefined {
    if (x < this.minX || x > this.maxX || z < this.minZ || z > this.maxZ) return undefined;
    const gx = Math.floor(x / CELL) - this.originCx;
    const gz = Math.floor(z / CELL) - this.originCz;
    if (gx < 0 || gx >= this.cols || gz < 0 || gz >= this.rows) return undefined;
    let best: number | undefined;
    const t = this.tris;
    for (let e = this.head[gz * this.cols + gx]; e !== -1; e = this.next[e]) {
      const o = this.tri[e] * 9;
      const ay = t[o + 1], by = t[o + 4], cy = t[o + 7];
      // Fast Y-bounds reject before barycentric division: if all vertices are
      // above `ceiling` or at/below `best`, the interpolated height cannot win.
      if (ay > ceiling && by > ceiling && cy > ceiling) continue;
      if (best !== undefined && ay <= best && by <= best && cy <= best) continue;
      const ax = t[o], az = t[o + 2], bx = t[o + 3], bz = t[o + 5], cx = t[o + 6], cz = t[o + 8];
      const det = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz);
      if (Math.abs(det) < 1e-9) continue;
      const w0 = ((bz - cz) * (x - cx) + (cx - bx) * (z - cz)) / det;
      const w1 = ((cz - az) * (x - cx) + (ax - cx) * (z - cz)) / det;
      const w2 = 1 - w0 - w1;
      if (w0 < -1e-6 || w1 < -1e-6 || w2 < -1e-6) continue;
      const y = w0 * ay + w1 * by + w2 * cy;
      if (y <= ceiling && (best === undefined || y > best)) best = y;
    }
    return best;
  }
}
